import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, formatBrandMoneyCompact, getBrandCurrencySuffix, getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {DollarSign, Search, Plus, Edit, Trash2, Package, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, Check, PauseCircle, Award, TrendingUp, TrendingDown, Calculator, Tag} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { inventoryValuationToCalcMethod } from './inventaireConfigDefaults';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { useAppData } from '../../core/contexts/AppDataContext';
import { fetchCatalogProducts, useCatalogProductsRefreshListener } from '../articles/catalogProductOptions';
import { calculatePMP, calculateFIFO, calculateLIFO, calculateCUMP } from '../../shared/utils/valuationMethods';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../shared/components/custom/CustomComponents.css';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { useTranslation } from 'react-i18next';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { CustomSelect, CustomNumberInput, CustomDatePicker } from '../../shared/components/custom';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';

registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

const ValuationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.valuationPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  const defaultCalcMethod = inventoryValuationToCalcMethod(inventoryConfig.valuationMethod);
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Classification dropdown
      const isClickOnClassButton = event.target.closest('[data-valuation-class-dropdown-button]');
      const isClickInClassDropdown = event.target.closest('[data-valuation-class-dropdown]');
      if (!isClickOnClassButton && !isClickInClassDropdown) {
        setShowClassDropdown(false);
      }
      
      // Method dropdown
      const isClickOnMethodButton = event.target.closest('[data-valuation-method-dropdown-button]');
      const isClickInMethodDropdown = event.target.closest('[data-valuation-method-dropdown]');
      if (!isClickOnMethodButton && !isClickInMethodDropdown) {
        setShowMethodDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { products: appProducts, stockItems: apiStockItems, fetchAllData } = useAppData();
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [valuations, setValuations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_products');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isClassFilterOpen, setIsClassFilterOpen] = useState(false);
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const [valuationMethod, setValuationMethod] = useState(defaultCalcMethod);
  const [isCalculating, setIsCalculating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;

  const EMPTY_FORM = {
    valuation_number: '',
    product_id: '',
    product_name: '',
    warehouse_id: '',
    valuation_method: defaultCalcMethod,
    quantity: '',
    unit_cost: '',
    valuation_date: new Date().toISOString().split('T')[0],
    classification: 'C',
    notes: '',
  };
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const classDropdownRef = useRef(null);
  const methodDropdownRef = useRef(null);
  const classFilterRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const loadCatalogProducts = useCallback(async () => {
    try {
      setCatalogProducts(await fetchCatalogProducts());
    } catch {
      setCatalogProducts([]);
    }
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch {
      setWarehouses([]);
    }
  }, []);

  useEffect(() => {
    void loadCatalogProducts();
    void loadWarehouses();
  }, [loadCatalogProducts, loadWarehouses]);

  useCatalogProductsRefreshListener(loadCatalogProducts);

  const products = appProducts;

  const stockItems = useMemo(() => {
    if (!apiStockItems || !Array.isArray(apiStockItems)) {
      return [];
    }
    return apiStockItems.map(item => {
      if (!item.unitPrice && item.productId) {
        const product = products.find(p => p.id === item.productId);
        if (product) {
          return { ...item, unitPrice: parseFloat(product.price) || 0 };
        }
      }
      return item;
    });
  }, [apiStockItems, products]);

  const getProductMovements = (product) => {
    if (!product || !product.id || !stockItems || !Array.isArray(stockItems)) {
      return [];
    }
    try {
      const movements = stockItems.filter(item => {
        if (!item) return false;
        if (item.productId === product.id) return true;
        if (item.items && Array.isArray(item.items)) {
          return item.items.some(i => i && i.id === product.id);
        }
        return false;
      });
      return movements.map(movement => ({
        ...movement,
        quantity: parseInt(movement.quantity) || 0,
        unitPrice: parseFloat(movement.unitPrice) || parseFloat(product.price) || 0,
        type: movement.type || 'unknown',
        date: movement.date || new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error in getProductMovements:', error);
      return [];
    }
  };

  const calculateProductValuation = useCallback((product, method) => {
    if (!product || !product.id) {
      return { unitPrice: 0, totalValue: 0, totalQuantity: 0 };
    }
    try {
      const productMovements = getProductMovements(product);
      let result;
      switch (method) {
        case 'FIFO': result = calculateFIFO(product, productMovements); break;
        case 'LIFO': result = calculateLIFO(product, productMovements); break;
        case 'CUMP': result = calculateCUMP(product, productMovements); break;
        case 'PMP':
        default: result = calculatePMP(product, productMovements); break;
      }
      return result;
    } catch (error) {
      console.error('Error calculating valuation:', error);
      return { unitPrice: 0, totalValue: 0, totalQuantity: 0 };
    }
  }, [stockItems]);

  useEffect(() => {
    calculateValuation();
  }, [valuationMethod, products]);

  const calculateValuation = useCallback(() => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      setValuations([]);
      setLoading(false);
      return;
    }
    
    setIsCalculating(true);
    setLoading(true);
    
    try {
      const valuatedProds = products.map(product => {
        const result = calculateProductValuation(product, valuationMethod);
        return {
          ...product,
          valuatedUnitPrice: result.unitPrice,
          totalValue: result.totalValue,
          totalQuantity: result.totalQuantity,
          classification: '',
          valuation_date: new Date().toISOString()
        };
      });
      
      const totalValue = valuatedProds.reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);
      const sortedProducts = [...valuatedProds].sort((a, b) => (parseFloat(b.totalValue) || 0) - (parseFloat(a.totalValue) || 0));
      
      let cumulativeValue = 0;
      let cumulativePercent = 0;
      const classifiedProducts = sortedProducts.map(product => {
        const value = parseFloat(product.totalValue) || 0;
        const percentOfTotal = totalValue > 0 ? (value / totalValue) * 100 : 0;
        
        // Calculate classification based on cumulative value BEFORE adding current product
        let classification;
        if (cumulativePercent < 80) {
          classification = 'A';
        } else if (cumulativePercent < 95) {
          classification = 'B';
        } else {
          classification = 'C';
        }
        
        // Update cumulative after classification
        cumulativeValue += value;
        cumulativePercent = totalValue > 0 ? (cumulativeValue / totalValue) * 100 : 0;
        
        return {
          ...product,
          classification,
          percentOfTotal: parseFloat(percentOfTotal.toFixed(2)),
          cumulativePercent: parseFloat(cumulativePercent.toFixed(2))
        };
      });
      
      setValuations(classifiedProducts);
    } catch (error) {
      console.error('Error in calculateValuation:', error);
    } finally {
      setIsCalculating(false);
      setLoading(false);
    }
  }, [products, valuationMethod, calculateProductValuation]);

  useEffect(() => { }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (classFilterRef.current && !classFilterRef.current.contains(event.target)) setIsClassFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stats = {
    total: valuations.length,
    classA: valuations.filter(v => v.classification === 'A').length,
    classB: valuations.filter(v => v.classification === 'B').length,
    classC: valuations.filter(v => v.classification === 'C').length,
    totalValue: valuations.reduce((sum, v) => sum + (parseFloat(v.totalValue) || 0), 0),
    avgValue: valuations.length > 0 ? (valuations.reduce((sum, v) => sum + (parseFloat(v.totalValue) || 0), 0) / valuations.length).toFixed(2) : 0
  };

  const mainTabs = [
    { id: 'all_products', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_classification', name: p('tab_by_classification'), icon: Shield },
    { id: 'par_methode', name: p('tab_by_method'), icon: Gauge }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'A', name: p('filter_class_a'), icon: Award },
    { id: 'B', name: p('filter_class_b'), icon: TrendingUp },
    { id: 'C', name: p('filter_class_c'), icon: TrendingDown }
  ];

  const statusOptions = [
    { value: 'A', label: p('filter_class_a') },
    { value: 'B', label: p('filter_class_b') },
    { value: 'C', label: p('filter_class_c') },
  ];

  const valuationDistribution = useMemo(() => ({
    all_products: {
      title: p('dist_overview_title'),
      data: [
        { id: 'class_a_high', filter: 'A', label: p('dist_class_a_high'), count: stats.classA, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'class_b_medium', filter: 'B', label: p('dist_class_b_medium'), count: stats.classB, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'class_c_low', filter: 'C', label: p('dist_class_c_low'), count: stats.classC, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    },
    par_classification: {
      title: p('dist_by_classification_title'),
      data: [
        { id: 'class_a_80', filter: 'A', label: p('dist_class_a_80'), count: stats.classA, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'class_b_15', filter: 'B', label: p('dist_class_b_15'), count: stats.classB, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'class_c_5', filter: 'C', label: p('dist_class_c_5'), count: stats.classC, color: 'bg-gradient-to-r from-gray-500 to-gray-600' }
      ]
    },
    par_methode: {
      title: p('dist_by_method_title'),
      data: [
        { id: 'method', filter: null, label: valuationMethod, count: valuations.length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'total_valued', filter: null, label: p('dist_total_valued'), count: stats.total, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    }
  }), [t, stats, valuations.length, valuationMethod]);

  const distributionInfo = valuationDistribution[mainViewMode] || valuationDistribution.all_products;

  const baseFilteredValuations = valuations.filter(v => {
    if (!inventaireRecordInDateRange(dateRange, v.valuation_date, v.created_at)) return false;

    // Search filter
    if (searchTerm && !v.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !v.reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_products') {
      if (quickFilter === 'A' && v.classification !== 'A') return false;
      if (quickFilter === 'B' && v.classification !== 'B') return false;
      if (quickFilter === 'C' && v.classification !== 'C') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_products':
          if (activeDistributionFilter === 'class_a_high' && v.classification !== 'A') return false;
          if (activeDistributionFilter === 'class_b_medium' && v.classification !== 'B') return false;
          if (activeDistributionFilter === 'class_c_low' && v.classification !== 'C') return false;
          break;
        case 'par_classification':
          if (activeDistributionFilter === 'class_a_80' && v.classification !== 'A') return false;
          if (activeDistributionFilter === 'class_b_15' && v.classification !== 'B') return false;
          if (activeDistributionFilter === 'class_c_5' && v.classification !== 'C') return false;
          break;
      }
    }
    
    return true;
  });

  // ============================================
  // 📤 EXPORT FUNCTIONALITY
  // ============================================

  // Export to CSV (JSON format)
  const handleExport = () => {
    const formatOptions = [
      { value: 'csv', label: p('csv') },
      { value: 'excel', label: p('excel') }
    ];

    const format = window.prompt(
      p('export_format_prompt', { formats: formatOptions.map(o => o.label).join(', ') }),
      'csv'
    );

    if (!format) return;
    
    // Define headers for export
    const headers = [
      { key: 'reference', label: p('col_reference') },
      { key: 'name', label: p('col_product') },
      { key: 'valuatedUnitPrice', label: p('col_unit_price') },
      { key: 'totalQuantity', label: p('col_quantity') },
      { key: 'totalValue', label: p('col_total_value') },
      { key: 'percentOfTotal', label: p('col_percent_total') },
      { key: 'classification', label: p('col_classification') }
    ];

    const dataToExport = filteredValuations;
    const fileName = `stock_valuation_${valuationMethod}_${new Date().toISOString().split('T')[0]}`;

    try {
      if (format.toLowerCase().includes('excel')) {
        exportToExcel(dataToExport, headers, `${fileName}.xlsx`);
      } else {
        exportToCSV(dataToExport, headers, `${fileName}.csv`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(p('error_export'));
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredValuations;
      
      // Create a temporary table element for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_reference')}</th>
                <th>${p('col_product')}</th>
                <th>${p('col_unit_price')}</th>
                <th>${p('col_quantity')}</th>
                <th>${p('col_total_value')}</th>
                <th>${p('col_classification')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.reference || `PROD-${item.id?.split('-')[1]?.substring(0, 8) || p('na')}`}</td>
                  <td>${item.name || p('na')}</td>
                  <td>${formatBrandMoney(item.valuatedUnitPrice || 0)}</td>
                  <td>${item.totalQuantity || '0'}</td>
                  <td>${formatBrandMoney(item.totalValue || 0)}</td>
                  <td>${item.classification || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      await generateReportPDF(p('export_report_title', { method: valuationMethod }), {});
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('error_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredValuations;
      
      // Create a temporary table element for Excel generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_reference')}</th>
                <th>${p('col_product')}</th>
                <th>${p('col_unit_price')}</th>
                <th>${p('col_quantity')}</th>
                <th>${p('col_total_value')}</th>
                <th>${p('export_col_percent')}</th>
                <th>${p('col_classification')}</th>
                <th>${p('export_col_method')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.reference || `PROD-${item.id?.split('-')[1]?.substring(0, 8) || 'N/A'}`}</td>
                  <td>${item.name || ''}</td>
                  <td>${formatBrandMoney(item.valuatedUnitPrice || 0, false)}</td>
                  <td>${item.totalQuantity || '0'}</td>
                  <td>${formatBrandMoney(item.totalValue || 0, false)}</td>
                  <td>${item.percentOfTotal || '0'}%</td>
                  <td>${item.classification || ''}</td>
                  <td>${valuationMethod}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      await generateReportExcel('stock_valuation', p('export_report_title', { method: valuationMethod }), {});
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('error_excel'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // ============================================
  // 📥 IMPORT FUNCTIONALITY
  // ============================================

  const handleImport = () => {
    alert(p('import_not_applicable'));
  };


  useEffect(() => {
    const method = inventoryValuationToCalcMethod(inventoryConfig.valuationMethod);
    setValuationMethod(method);
    setFormData((prev) => ({ ...prev, valuation_method: method }));
  }, [inventoryConfig.valuationMethod]);

  const generateValuationNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'valuation', 'VAL', valuations, (v) => v.valuation_number),
    [settings, valuations],
  );

  const openNewValuationModal = () => {
    if (!inventoryConfig.enableValuation) {
      window.alert(p('notice_valuation_disabled', { defaultValue: 'تقييم المخزون معطّل من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setFormData({
      ...EMPTY_FORM,
      valuation_method: valuationMethod,
      valuation_number: inventoryConfig.autoGenerateValuationReference !== false ? generateValuationNumber() : '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, valuation_method: valuationMethod });
    setEditingRecord(null);
    setShowModal(false);
  };

  const handleProductSelect = (productId) => {
    const pickerProducts = catalogProducts.length > 0 ? catalogProducts : appProducts;
    const product = pickerProducts.find((prod) => String(prod.id) === String(productId));
    if (!product) {
      setFormData((prev) => ({ ...prev, product_id: productId || '', product_name: '' }));
      return;
    }
    const result = calculateProductValuation(product, formData.valuation_method || valuationMethod);
    setFormData((prev) => ({
      ...prev,
      product_id: productId,
      product_name: product.name || '',
      quantity: result.totalQuantity != null ? String(result.totalQuantity) : '',
      unit_cost: result.unitPrice != null ? String(result.unitPrice) : '',
      classification: product.classification || prev.classification,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id && !formData.product_name.trim()) {
      window.alert(p('select'));
      return;
    }
    const qty = parseFloat(formData.quantity) || 0;
    const unitCost = parseFloat(formData.unit_cost) || 0;
    try {
      setSaving(true);
      const valuationNumber = (formData.valuation_number || '').trim() || generateValuationNumber();
      const payload = {
        valuation_number: valuationNumber,
        product_id: formData.product_id || null,
        product_name: formData.product_name.trim(),
        warehouse_id: formData.warehouse_id || null,
        valuation_method: formData.valuation_method || valuationMethod,
        quantity: qty,
        unit_cost: unitCost,
        total_value: qty * unitCost,
        valuation_date: formData.valuation_date || new Date().toISOString().split('T')[0],
        classification: formData.classification || 'C',
        notes: formData.notes || null,
      };
      if (editingRecord?.valuation_id) {
        await localApiAdapter.put(`/inventory/valuations/${editingRecord.valuation_id}`, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التقييم' }));
      } else {
        await localApiAdapter.post('/inventory/valuations', payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التقييم' }));
      }
      resetForm();
      await fetchAllData?.();
      calculateValuation();
    } catch (error) {
      console.error('Error saving valuation:', error);
      notifyError(showNotify, error?.response?.data?.message || p('error_export'));
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleWarehouseSelect = (warehouseId) => {
    setFormData((prev) => ({
      ...prev,
      ...mapInventaireWarehouseSelection(warehouseId, warehouses),
    }));
  };

  const productOptions = useMemo(
    () => buildInventaireProductSelectOptions(catalogProducts.length > 0 ? catalogProducts : appProducts),
    [catalogProducts, appProducts],
  );

  const warehouseOptions = useMemo(
    () => buildInventaireWarehouseSelectOptions(warehouses),
    [warehouses],
  );

  const methodOptions = useMemo(() => [
    { value: 'PMP', label: p('method_pmp') },
    { value: 'FIFO', label: p('method_fifo') },
    { value: 'LIFO', label: p('method_lifo') },
    { value: 'CUMP', label: p('method_cump') },
  ], [p]);

  const classificationOptions = [
    { value: 'A', label: p('class_a'), icon: Award, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'B', label: p('class_b'), icon: TrendingUp, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'C', label: p('class_c'), icon: TrendingDown, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ];

  // Simple non-clickable classification badge
  const getClassificationBadge = (classification) => {
    const config = classificationOptions.find(c => c.value === classification) || classificationOptions[2];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  const getMethodShortDesc = (method) => {
    switch (method) {
      case 'PMP': return p('method_desc_pmp');
      case 'FIFO': return p('method_desc_fifo');
      case 'LIFO': return p('method_desc_lifo');
      case 'CUMP': return p('method_desc_cump');
      default: return method;
    }
  };

  const getMethodFullDesc = (method) => {
    switch (method) {
      case 'PMP': return p('method_full_pmp');
      case 'FIFO': return p('method_full_fifo');
      case 'LIFO': return p('method_full_lifo');
      case 'CUMP': return p('method_full_cump');
      default: return method;
    }
  };

  const getClassCardInterpretation = (classification) => {
    if (classification === 'A') return p('interp_class_a');
    if (classification === 'B') return p('interp_class_b');
    return p('interp_class_c');
  };

  const getClassModalInterpretation = (classification) => {
    if (classification === 'A') return p('interp_modal_a');
    if (classification === 'B') return p('interp_modal_b');
    return p('interp_modal_c');
  };

  const getStockStatusLabel = (quantity) => {
    if (quantity > 100) return p('stock_high');
    if (quantity > 50) return p('stock_normal');
    if (quantity > 0) return p('stock_low');
    return p('stock_out');
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'reference', labelKey: 'col_reference', getDisplay: (r) => r.valuation_number || r.reference || '' },
    { key: 'name', labelKey: 'col_product' },
    { key: 'valuatedUnitPrice', labelKey: 'col_unit_price' },
    { key: 'totalQuantity', labelKey: 'col_quantity' },
    { key: 'total_value', labelKey: 'col_total_value' },
    { key: 'percentOfTotal', labelKey: 'col_percent_total' },
    { key: 'classification', labelKey: 'col_classification' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const columnFilterRules = useMemo(() => [
    textColumnRule('reference', (r) => r.valuation_number || r.reference || ''),
    textColumnRule('name'),
    textColumnRule('valuatedUnitPrice', (r) => String(r.valuatedUnitPrice ?? '')),
    textColumnRule('totalQuantity', (r) => String(r.totalQuantity ?? '')),
    textColumnRule('total_value', (r) => String(r.totalValue ?? r.total_value ?? '')),
    textColumnRule('percentOfTotal', (r) => String(r.percentOfTotal ?? '')),
    exactColumnRule('classification', (r) => r.classification),
  ], []);

  const filteredValuations = useMemo(
    () => applyColumnFilters(baseFilteredValuations, colFilters, columnFilterRules),
    [baseFilteredValuations, colFilters, columnFilterRules],
  );
  const visibleValuations = useMemo(
    () => sliceInventaireListItems(filteredValuations, itemsPerPage),
    [filteredValuations, itemsPerPage],
  );

  const filteredValuationsIds = useMemo(() => filteredValuations.map((r) => r.id), [filteredValuations]);
  const allRowsSelected = filteredValuationsIds.length > 0 && filteredValuationsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredValuationsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (!selectedRecords.length) return;
    if (!window.confirm(p('delete_count', { count: selectedRecords.length }))) return;
    try {
      setSaving(true);
      await Promise.all(
        selectedRecords.map((id) => localApiAdapter.delete(`/inventory/valuations/${id}`).catch(() => null)),
      );
      notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'التقييم' }));
      setSelectedRecords([]);
      calculateValuation();
    } catch (error) {
      console.error('Bulk delete error:', error);
      notifyError(showNotify, p('error_export'));
    } finally {
      setSaving(false);
    }
  };

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: DollarSign },
    { key: 'class_a', label: p('dist_class_a_high'), value: stats.classA, icon: Package },
    { key: 'total_value', label: p('col_total_value'), value: stats.totalValue, icon: TrendingUp },
    { key: 'stat_avg', label: p('stat_avg'), value: stats.avgValue, icon: Gauge },
  ], 4), [stats, p]);


  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableValuation ? p('notice_valuation_disabled_banner', { defaultValue: 'تقييم المخزون معطّل — فعّله من إعدادات المخزون ← تقييم المخزون.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={DollarSign}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewValuationModal}
      createLabel={p('new_valuation')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('reference') && (

                  
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_reference')} value={colFilters.reference ?? ''} onChange={(v) => setColFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('name') && (

                    <TableColumnHeaderFilter columnKey="name" label={p('col_product')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('valuatedUnitPrice') && (

                    <TableColumnHeaderFilter columnKey="valuatedUnitPrice" label={p('col_unit_price')} value={colFilters.valuatedUnitPrice ?? ''} onChange={(v) => setColFilter('valuatedUnitPrice', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('totalQuantity') && (

                    <TableColumnHeaderFilter columnKey="totalQuantity" label={p('col_quantity')} value={colFilters.totalQuantity ?? ''} onChange={(v) => setColFilter('totalQuantity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('total_value') && (

                    <TableColumnHeaderFilter columnKey="total_value" label={p('col_total_value')} value={colFilters.total_value ?? ''} onChange={(v) => setColFilter('total_value', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('percentOfTotal') && (

                    <TableColumnHeaderFilter columnKey="percentOfTotal" label={p('col_percent_total')} value={colFilters.percentOfTotal ?? ''} onChange={(v) => setColFilter('percentOfTotal', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('classification') && (

                    <TableColumnHeaderFilter columnKey="classification" label={p('col_classification')} value={colFilters.classification ?? ''} onChange={(v) => setColFilter('classification', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                                    {/* Actions - Not sortable */}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleValuations.length > 0 ? (
                  visibleValuations.map((record, index) => {
                    // Helper function to get adaptive light text under reference
                    const getAdaptiveLightText = () => {
                      if (record.barcode) {
                        return p('barcode_label', { code: record.barcode });
                      }
                      return '';
                    };

                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''} ${index === 0 ? 'first:rounded-t-3xl' : ''} ${index === visibleValuations.length - 1 ? 'last:rounded-b-3xl' : ''}`}>
                        <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                        
                        {/* Référence - with adaptive details */}
                        {isColVisible('reference') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">
                              {record.valuation_number || record.reference || `PROD-${record.id?.split('-')[1]?.substring(0, 8) || 'N/A'}`}
                            </span>
                            <span className="text-white/50 text-xs mt-0.5">{getAdaptiveLightText()}</span>
                          </div>
                        </td>

                        )}
                        
                        {/* Produit - Name bold, category light */}
                        {isColVisible('name') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('category_label', { name: record.category || p('standard') })}</span>
                          </div>
                        </td>

                        )}
                        
                        {/* Prix Unitaire - bold value, light method */}
                        {isColVisible('valuatedUnitPrice') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.valuatedUnitPrice || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('calculated_by', { method: valuationMethod })}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* Quantité - bold value, light stock status */}
                        {isColVisible('totalQuantity') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.totalQuantity || 0}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {getStockStatusLabel(record.totalQuantity)}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* Valeur Totale - bold value, light percentage */}
                        {isColVisible('total_value') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.totalValue || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('percent_of_total', { percent: ((record.totalValue / stats.totalValue) * 100).toFixed(2) })}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {/* % du Total - bold percentage */}
                        {isColVisible('percentOfTotal') && (

                          <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.percentOfTotal}%</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('of_total')}
                            </span>
                          </div>
                        </td>

                        )}
                        
                        {isColVisible('classification') && (

                        
                          <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.classification} recordId={record.id} options={classificationOptions} title={p('abc_auto_title')} /></div></td>

                        
                        )}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            viewTitle={p('view')}
                          />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Package size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View - Expanded */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleValuations.length > 0 ? (
              visibleValuations.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Package size={20} className="text-emerald-400" />
                          {record.name}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.classification} recordId={record.id} options={classificationOptions} title={p('abc_auto_title')} /></div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {record.reference || `PROD-${record.id?.split('-')[1]?.substring(0, 8) || 'N/A'}`}
                        </span>
                      </div>
                      
                      <div className={INVENTAIRE_FORM_GRID}>
                        {/* Informations Produit */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}</span> {record.reference || `PROD-${record.id?.split('-')[1]?.substring(0, 8) || p('na')}`}</div>
                            <div><span className="font-medium">{p('label_category')}</span> {record.category || p('standard')}</div>
                            <div><span className="font-medium">{p('label_unit')}</span> {record.unit || p('unit_default')}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium text-white">{p('label_description')}</span>
                              <div className="text-white/90 text-xs mt-1">{record.description || p('no_description')}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_valuation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <DollarSign size={16} className="text-blue-400" />
                              <span className="font-medium">{p('label_unit_price')}</span> <span className="text-white">{formatBrandMoney(record.valuatedUnitPrice || 0)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Package size={16} className="text-yellow-400" />
                              <span className="font-medium">{p('label_quantity')}</span> <span className="text-white">{record.totalQuantity || 0}</span>
                            </div>
                            <div className="pt-2">
                              <span className="font-medium">{p('label_total_value')}</span>
                              <div className="text-white font-bold text-lg mt-1">
                                {formatBrandMoney(record.totalValue || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Classification ABC */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_abc')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('label_class')}</span>
                              <p className="text-blue-200 text-lg font-bold mt-1">{record.classification}</p>
                            </div>
                            <div><span className="font-medium">{p('label_percent_total')}</span> {record.percentOfTotal}%</div>
                            <div><span className="font-medium">{p('label_cumulative_percent')}</span> {record.percentOfTotal}%</div>
                            <p className="text-white/40 text-xs italic mt-2">
                              {getClassCardInterpretation(record.classification)}
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_calc_method')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>
                              <span className="font-medium">{p('label_method')}</span> {valuationMethod}
                            </div>
                            <div>
                              <span className="font-medium">{p('label_calc_date')}</span>
                              <div className="text-white/90 text-xs mt-1">
                                {new Date().toLocaleString(dateLocale)}
                              </div>
                            </div>
                            <div className="mt-2 p-2 bg-white/5 rounded">
                              <span className="font-medium text-white/80 text-xs">{p('label_info')}</span>
                              <p className="text-white/60 text-xs mt-1">
                                {getMethodShortDesc(valuationMethod)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      viewTitle={p('view')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('new_valuation') : p('new_valuation')}
          icon={DollarSign}
          formId="valuation-form"
          saveLabel={t('common.save')}
          showSaveButton
          saving={saving}
          maxWidth="900px"
          testId="valuation-crud-modal"
        >
          <form id="valuation-form" onSubmit={handleSubmit}>
            <div className={`${INVENTAIRE_FORM_GRID} mt-4`}>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_valuation_number', { defaultValue: 'رمز التقييم' })}</label>
                <input
                  type="text"
                  required
                  value={formData.valuation_number || ''}
                  onChange={(e) => setFormData({ ...formData, valuation_number: e.target.value })}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  placeholder={p('placeholder_valuation_number', { defaultValue: 'VAL-001' })}
                  dir="ltr"
                />
              </div>
              <div className="md:col-span-2">
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_product')}</label>
                <CustomSelect
                  value={formData.product_id}
                  onChange={handleProductSelect}
                  options={productOptions}
                  placeholder={p('select')}
                  searchable
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_warehouse', { defaultValue: 'المستودع' })}</label>
                <CustomSelect
                  value={formData.warehouse_id}
                  onChange={handleWarehouseSelect}
                  options={warehouseOptions}
                  placeholder={p('select')}
                  searchable
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_quantity')}</label>
                <CustomNumberInput
                  value={formData.quantity}
                  onChange={(val) => setFormData({ ...formData, quantity: val })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_unit_price')}</label>
                <CustomNumberInput
                  value={formData.unit_cost}
                  onChange={(val) => setFormData({ ...formData, unit_cost: val })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_method')}</label>
                <CustomSelect
                  value={formData.valuation_method}
                  onChange={(val) => setFormData({ ...formData, valuation_method: val })}
                  options={methodOptions}
                  placeholder={p('select')}
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_classification')}</label>
                <CustomSelect
                  value={formData.classification}
                  onChange={(val) => setFormData({ ...formData, classification: val })}
                  options={classificationOptions.map((c) => ({ value: c.value, label: c.label }))}
                  placeholder={p('select')}
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_calculation_date')}</label>
                <CustomDatePicker
                  value={formData.valuation_date}
                  onChange={(date) => setFormData({ ...formData, valuation_date: date })}
                />
              </div>
              <div className="md:col-span-3">
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_description')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className={cfgTheme.input}
                  placeholder={p('no_description')}
                />
              </div>
            </div>
          </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('modal_title')}
          icon={DollarSign}
          maxWidth="1100px"
          testId="valuation-view-modal"
        >
          <div className="space-y-4">
            <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
              <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                <Package size={18} className="text-emerald-400" />
                {p('section_product_info')}
              </h3>
              <div className={INVENTAIRE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_product_name')}</p>
                  <p className={`font-medium text-lg ${cfgTheme.title}`}>{viewingRecord.name}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_reference')}</p>
                  <p className={`font-medium font-mono text-sm ${cfgTheme.title}`}>{viewingRecord.valuation_number || viewingRecord.reference || '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_category').replace(':', '')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.category || p('standard')}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_unit')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.unit || p('unit_default')}</p>
                </div>
                {viewingRecord.description && (
                  <div className="md:col-span-4">
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_description')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.description}</p>
                  </div>
                )}
              </div>
            </div>

            <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
              <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                <DollarSign size={18} className="text-blue-400" />
                {p('section_stock_valuation')}
              </h3>
              <div className={INVENTAIRE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_unit_price')}</p>
                  <p className={`font-medium text-lg ${cfgTheme.title}`}>{formatBrandMoney(viewingRecord.valuatedUnitPrice || 0)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_stock_qty')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.totalQuantity || 0}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_total_value')}</p>
                  <p className={`font-medium text-lg ${cfgTheme.title}`}>{formatBrandMoney(viewingRecord.totalValue || 0)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_percent_total')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.percentOfTotal}%</p>
                </div>
              </div>
            </div>

            <div className={INVENTAIRE_FORM_GRID}>
              <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
                <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                  <Award size={18} className="text-yellow-400" />
                  {p('section_abc')}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_class')}</p>
                    <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.classification} recordId={viewingRecord.id} options={classificationOptions} title={p('abc_auto_title')} /></div>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_percent_of_total')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.percentOfTotal}%</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_interpretation')}</p>
                    <p className={`font-medium text-xs ${cfgTheme.title}`}>
                      {getClassModalInterpretation(viewingRecord.classification)}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
                <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                  <Calculator size={18} className="text-red-400" />
                  {p('section_calc_method')}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_method_used')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{valuationMethod}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_method_description')}</p>
                    <p className={`font-medium text-xs ${cfgTheme.title}`}>
                      {getMethodFullDesc(valuationMethod)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_calculation_date')}</p>
                    <p className={`font-medium text-xs ${cfgTheme.title}`}>{new Date().toLocaleString(dateLocale)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default ValuationPage;


